import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './SearchableSelect.module.css'
import { CheckIcon, ChevronIcon, SearchIcon } from '../../icons'

export interface SearchableSelectOption {
  value: string
  label: string
  keywords?: string[]
}

export interface SearchableSelectProps {
  value: string
  options: SearchableSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  disabledAppearance?: 'default' | 'muted'
  className?: string
  menuClassName?: string
  emptyText?: string
  'aria-label'?: string
}

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase()
}

function resolveInitialActiveIndex(options: SearchableSelectOption[], value: string): number {
  if (options.length === 0) {
    return -1
  }

  const selectedIndex = options.findIndex((option) => option.value === value)
  return selectedIndex >= 0 ? selectedIndex : 0
}

export const SearchableSelect = forwardRef<HTMLDivElement, SearchableSelectProps>(
  (
    {
      value,
      options,
      onChange,
      placeholder = 'Select',
      searchPlaceholder = 'Search',
      disabled = false,
      disabledAppearance = 'default',
      className,
      menuClassName,
      emptyText = 'No results',
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [activeIndex, setActiveIndex] = useState(-1)
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 })
    const optionIdBase = useId().replace(/:/g, '')
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([])

    const selectedOption = useMemo(
      () => options.find((option) => option.value === value),
      [options, value]
    )

    const filteredOptions = useMemo(() => {
      const normalizedQuery = normalizeSearchValue(query)
      if (!normalizedQuery) {
        return options
      }

      return options.filter((option) => {
        const searchable = [option.label, option.value, ...(option.keywords ?? [])]
          .join(' ')
          .toLowerCase()
        return searchable.includes(normalizedQuery)
      })
    }, [options, query])

    const updateMenuPosition = useCallback(() => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) {
        return
      }
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.min(Math.max(rect.width, 194), 242),
      })
    }, [])

    const openMenu = useCallback(() => {
      if (disabled) {
        return
      }
      updateMenuPosition()
      setActiveIndex(resolveInitialActiveIndex(options, value))
      setIsOpen(true)
    }, [disabled, options, updateMenuPosition, value])

    const closeMenu = useCallback(() => {
      setIsOpen(false)
      setQuery('')
      setActiveIndex(-1)
    }, [])

    const handleSelect = useCallback(
      (nextValue: string) => {
        onChange(nextValue)
        closeMenu()
      },
      [closeMenu, onChange]
    )

    const moveActiveOption = useCallback(
      (direction: 1 | -1) => {
        setActiveIndex((currentIndex) => {
          const optionCount = filteredOptions.length
          if (optionCount === 0) {
            return -1
          }
          if (currentIndex < 0) {
            return direction > 0 ? 0 : optionCount - 1
          }
          return (currentIndex + direction + optionCount) % optionCount
        })
      },
      [filteredOptions.length]
    )

    const handleSearchKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          moveActiveOption(1)
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          moveActiveOption(-1)
          return
        }

        if (event.key === 'Enter') {
          const activeOption = filteredOptions[activeIndex]
          if (!activeOption) {
            return
          }
          event.preventDefault()
          handleSelect(activeOption.value)
        }
      },
      [activeIndex, filteredOptions, handleSelect, moveActiveOption]
    )

    useEffect(() => {
      if (!isOpen) {
        return
      }

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        if (
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          closeMenu()
        }
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeMenu()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }, [closeMenu, isOpen])

    useEffect(() => {
      if (!isOpen) {
        return
      }

      const handleUpdate = () => updateMenuPosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      requestAnimationFrame(() => searchInputRef.current?.focus())
      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }, [isOpen, updateMenuPosition])

    useEffect(() => {
      if (!isOpen) {
        return
      }

      setActiveIndex((currentIndex) => {
        if (filteredOptions.length === 0) {
          return -1
        }
        if (currentIndex >= 0 && currentIndex < filteredOptions.length) {
          return currentIndex
        }
        return resolveInitialActiveIndex(filteredOptions, value)
      })
    }, [filteredOptions, isOpen, value])

    useEffect(() => {
      if (!isOpen || activeIndex < 0) {
        return
      }

      optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex, isOpen])

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value)
      setActiveIndex(0)
    }

    const activeOptionId =
      activeIndex >= 0 && filteredOptions[activeIndex]
        ? `${optionIdBase}-option-${activeIndex}`
        : undefined

    const menuContent = isOpen ? (
      <div
        ref={menuRef}
        className={`${styles.menu} ${menuClassName ?? ''}`}
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
        }}
      >
        <div className={styles.searchWrapper}>
          <div className={styles.searchBox}>
            <SearchIcon className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              className={styles.searchInput}
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleSearchKeyDown}
              placeholder=""
              aria-label={searchPlaceholder}
              aria-controls={`${optionIdBase}-listbox`}
              aria-activedescendant={activeOptionId}
            />
          </div>
        </div>
        <ul id={`${optionIdBase}-listbox`} className={styles.optionList} role="listbox">
          {filteredOptions.map((option, index) => {
            const isSelected = option.value === value
            const isActive = index === activeIndex
            return (
              <li key={option.value}>
                <button
                  id={`${optionIdBase}-option-${index}`}
                  ref={(node) => {
                    optionRefs.current[index] = node
                  }}
                  type="button"
                  className={`${styles.option} ${isSelected ? styles.optionActive : ''} ${isActive ? styles.optionHighlighted : ''}`}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {isSelected && <CheckIcon width={16} height={16} className={styles.checkIcon} />}
                </button>
              </li>
            )
          })}
          {filteredOptions.length === 0 && <li className={styles.empty}>{emptyText}</li>}
        </ul>
      </div>
    ) : null

    return (
      <div ref={ref} className={`${styles.wrapper} ${className ?? ''}`}>
        <button
          ref={triggerRef}
          type="button"
          className={`${styles.trigger} ${
            disabled && disabledAppearance === 'muted' ? styles.triggerMuted : ''
          }`}
          onClick={() => (isOpen ? closeMenu() : openMenu())}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={ariaLabel ?? placeholder}
        >
          <span className={`${styles.value} ${selectedOption ? '' : styles.placeholder}`}>
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronIcon
            direction={isOpen ? 'up' : 'down'}
            className={styles.chevron}
            width={12}
            height={6}
          />
        </button>
        {menuContent && createPortal(menuContent, document.body)}
      </div>
    )
  }
)

SearchableSelect.displayName = 'SearchableSelect'
